import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQueries, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  assetControllerGetAssetQueryOptions,
  customerControllerGetCustomerQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { formatZonedDateTime } from '../lib/date-formatting';
import { Group, Stack } from '@folio/components/ui/flex';

/**
 * Identity-bearing confirmation: the actor and their business role are on the submit button, so a
 * termination is never one anonymous click. No step-up auth — the identity is recorded, not proven.
 */
export function StopConfirmation({
  reasonLabel,
  stopDate,
  formId,
  loading,
  onBack,
}: {
  reasonLabel: string;
  stopDate?: string;
  formId: string;
  loading: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { policyOnMain, workingBranch } = usePolicyOnDashboard();

  const { data: user } = useSuspenseQuery(legacyPermissionsControllerAtMeQueryOptions());
  // Same query options as PolicyDashboardHeader, branchId included: a shorter key misses its cache
  // and refetches every customer when the dialog opens.
  const customers = useQueries({
    queries: policyOnMain.customers.map(customerId =>
      customerControllerGetCustomerQueryOptions(customerId, { branchId: workingBranch?.id }),
    ),
  });

  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policyOnMain.productId];
  const dashboardAssetFieldKey = productConfig?.dashboardAssetFieldKey;
  const customerInformations = usePolicyProductConfig('customerInformations');

  const firstAssetId = policyOnMain.assets[0];
  const { data: firstAsset } = useQuery({
    ...assetControllerGetAssetQueryOptions(firstAssetId!, { branchId: workingBranch?.id }),
    enabled: !!firstAssetId && !!dashboardAssetFieldKey,
  });

  const assetLabel = dashboardAssetFieldKey
    ? firstAsset?.customFields?.find(({ key }) => key === dashboardAssetFieldKey)?.value
    : undefined;

  const businessRoleId =
    user.brokerRole ??
    user.relations.find(({ resourceId }) => policyOnMain.customers.includes(resourceId))
      ?.relationType;

  const actor = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  const actorRole = businessRoleId
    ? t([`businessRoles.${businessRoleId}.label`, businessRoleId])
    : undefined;

  return (
    <>
      <Stack asChild gap={2}>
        <dl className="text-sm">
          <Row
            label={t('terminationButton.confirm.policyholder')}
            value={
              customerDisplayName(
                customers.flatMap(({ data }) => data ?? []),
                customerInformations?.fieldsConfig,
                productConfig?.customerDefaultRole,
              ) ?? ''
            }
          />
          {!!assetLabel && (
            <Row label={t('terminationButton.confirm.asset')} value={String(assetLabel)} />
          )}
          <Row label={t('terminationButton.confirm.reason')} value={reasonLabel} />
          <Row
            label={t('terminationButton.confirm.effectiveDate')}
            value={
              stopDate
                ? formatZonedDateTime(new Date(stopDate), policyOnMain.timeZone)
                : t('terminationButton.confirm.noDate')
            }
          />
        </dl>
      </Stack>
      <Group align="stretch" justify="between" gap={2}>
        <Button type="button" variant="outline" onClick={onBack}>
          {t('terminationButton.confirm.back')}
        </Button>
        <Button type="submit" form={formId} loading={loading}>
          {actorRole
            ? t('terminationButton.confirm.submitWithRole', { actor, role: actorRole })
            : t('terminationButton.confirm.submit', { actor })}
        </Button>
      </Group>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Group align="stretch" justify="between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </Group>
  );
}
